import { useQuery } from '@tanstack/react-query';
import sharedHelpers, { createQueryParams } from '@gloat/helpers';
import { CompanyConfiguration, CompanyData, MentorshipProfile } from '@gloat/types';
import { apiAuthClient } from '../root/api-auth-client';

export const companyQueryKey = 'company';

export interface CompanyType {
  featureVariations: CompanyConfiguration;
  company: CompanyData;
  mentorshipProfile: MentorshipProfile;
}

const useCompany = (props?: { enabled?: boolean }) => {
  const { enabled } = props || {};
  const companySlug = sharedHelpers?.getCompanySlugFromHost?.() || '';
  return useQuery<CompanyType>({
    queryKey: [companyQueryKey],

    queryFn: async () => {
      const res = await apiAuthClient.fetch(`/api/v1/company-configuration/name/${companySlug}`);
      if (!res.ok) {
        throw new Error(`Network response was not ok for company '${companySlug}', status: ${res.status}`);
      }
      const data = await res.json();
      return {
        ...data,
        company: data?.company || {},
        featureVariations: data?.featureVariations || {},
        mentorshipProfile: data?.mentorshipProfile || {},
      };
    },

    enabled,
  });
};

type GetCompanyApplicationsParams = {
  q?: string;
  page?: number;
  limit?: number;
  validateCompanyExists?: boolean;
};

type CompanyApplication = {
  id: string;
  name: string;
  category: string;
  description: string;
};

type GetCompanyApplicationsResponse = {
  page: number;
  page_size: number;
  total_count: number;
  page_count: number;
  has_next: boolean;
  items: CompanyApplication[];
};

async function getCompanyApplications(params: GetCompanyApplicationsParams): Promise<GetCompanyApplicationsResponse> {
  const queryParams = createQueryParams({
    ...params,
    validate_company_exists: params.validateCompanyExists,
  });
  const queryString = queryParams ? `?${queryParams.toString()}` : '';

  const response = await apiAuthClient.fetch(`/api/company/v1/applications${queryString}`);

  if (!response.ok) {
    throw new Error('Error getting company applications');
  }

  return response.json();
}

export function useCompanyApplications(params: GetCompanyApplicationsParams, options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: [companyQueryKey, 'applications'],
    queryFn: () => getCompanyApplications(params),
    enabled: options?.enabled,
  });
}

export default useCompany;
